import { getAccessToken } from '@nexus/supabase';

import { EdgeFetchError } from './supabase-edge-fetch';

export interface MemoryEntry {
  id: string;
  text: string;
}

export interface MemorySection {
  /** Mem0 category key — the frontend translates it to a heading. */
  category: string;
  /** English default heading (used when no translation exists). */
  title: string;
  entries: MemoryEntry[];
}

/** The server rejects longer text with 400. */
export const MEMORY_TEXT_MAX_LENGTH = 500;

export interface SavedMemory {
  id: string;
  text: string;
  /** Visible topics only, in display order. `titles` lines up with it. */
  categories: string[];
  titles: string[];
}

export interface MemoryDocument {
  sections: MemorySection[];
  updatedAt: string | null;
}

/** A backend that predates `entries` omits it. */
interface MemoryResponse {
  sections?: Array<Omit<MemorySection, 'entries'> & { entries?: MemoryEntry[] }>;
  updatedAt?: string | null;
}

const MEMORY_ERROR_KEYS: Record<string, string> = {
  '422:no_topic': 'settings.memory.noTopic',
  '409:memory_busy': 'settings.memory.busy',
  '409:memory_blocked': 'settings.memory.blocked',
};

/** The translation key that tells the user why a memory request failed. */
export function memoryErrorKey(error: unknown): string {
  const key = error instanceof EdgeFetchError ? MEMORY_ERROR_KEYS[`${error.status}:${error.code}`] : undefined;
  return key ?? 'settings.memory.actionFailed';
}

async function memoryRequest(baseUrl: string, path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new Error('No authenticated session');

  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: { ...init.headers, Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { code?: unknown } | null;
    throw new EdgeFetchError(
      `Memory request failed (${response.status})`,
      response.status,
      typeof body?.code === 'string' ? body.code : undefined
    );
  }
  return response;
}

/**
 * Fetch the authenticated user's memories from the Mem0-backed edge function.
 * Mirrors fetchMyEntitlements: the caller passes its app-configured auth
 * backend base URL; the user is resolved server-side from the bearer token.
 */
export async function getMemory(baseUrl: string): Promise<MemoryDocument> {
  const response = await memoryRequest(baseUrl, '/memories');

  const data = (await response.json()) as MemoryResponse;
  return {
    sections: (data.sections ?? []).map(({ category, title, entries = [] }) => ({ category, title, entries })),
    updatedAt: data.updatedAt ?? null,
  };
}

export async function addMemory(baseUrl: string, text: string): Promise<SavedMemory> {
  const response = await memoryRequest(baseUrl, '/memories', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
  return (await response.json()) as SavedMemory;
}

export async function updateMemory(baseUrl: string, id: string, text: string): Promise<void> {
  await memoryRequest(baseUrl, `/memories/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
}

export async function deleteMemory(baseUrl: string, id: string): Promise<void> {
  await memoryRequest(baseUrl, `/memories/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function clearMemories(baseUrl: string): Promise<void> {
  await memoryRequest(baseUrl, '/memories', { method: 'DELETE' });
}
